import { components, paths } from "@/api-schema";
import {
  PathsV1CustomerSearchPostParametersQueryExtend,
  PathsV1CustomerSearchPostParametersQueryOrderByDirection,
  PathsV1CustomerSearchPostParametersQueryOrderByField,
  SubjectRoles,
  DataExportType,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useCustomerSearchQuery } from "@/common/api/customer/use-customer-search.query";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures, TimestampSortField } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { CompanyGroupFilter } from "@/components/filter/predefined/company-group-filter";
import { CustomerIdentifierTypeFilter } from "@/components/filter/predefined/customer-identifier-type-filter";
import { CustomerTypeFilter } from "@/components/filter/predefined/customer-type-filter";
import { CustomerCompanyGroupScopeEmptyState } from "@/modules/masterdata/customer/_components/customer-company-group-scope-empty-state";
import {
  getUserCompanyGroupTechnicalIds,
  hasCustomerCompanyGroupScope,
  hasUnrestrictedCustomerAccess,
} from "@/modules/masterdata/customer/customer.permissions";
import { userStore } from "@/store/user-store";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "../_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "../_components/base-predefined-table.types";
import { customerTableDefaultColumns } from "./customer-table-columns";

type Customer = components["schemas"]["Customer"] & RowWithIdentifier;
type CustomerSorting = NonNullable<paths["/v1/customer/search"]["post"]["parameters"]["query"]["orderBy"]>;
type CustomerQueryParams = paths["/v1/customer/search"]["post"]["parameters"]["query"];
type CustomerRequestBody = paths["/v1/customer/search"]["post"]["requestBody"]["content"]["application/json"];
type CustomerExtend = NonNullable<paths["/v1/customer/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: CustomerExtend = [
  PathsV1CustomerSearchPostParametersQueryExtend.Addresses,
  PathsV1CustomerSearchPostParametersQueryExtend.Addresses_Address,
  PathsV1CustomerSearchPostParametersQueryExtend.CustomerIdentifiers,
] as const;

const DEFAULT_COLUMNS = [
  customerTableDefaultColumns.columnName,
  customerTableDefaultColumns.columnNameAndContact,
  customerTableDefaultColumns.columnAddress,
  customerTableDefaultColumns.columnContact,
  customerTableDefaultColumns.columnIds,
  customerTableDefaultColumns.columnType,
];

const CUSTOMER_TABLE_CONFIG: PredefinedTableConfig<CustomerSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1CustomerSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1CustomerSearchPostParametersQueryOrderByField.LastName,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("masterdata:SearchCustomers"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  sortingMapping: {
    lastName: ["lastName"],
    type: ["type"],
  } as const,
  sortColumns: [
    { id: PathsV1CustomerSearchPostParametersQueryOrderByField.LastName, label: () => i18next.t("common:Name") },
    {
      id: PathsV1CustomerSearchPostParametersQueryOrderByField.Type,
      label: () => i18next.t("masterdata:CustomerType"),
    },
  ],
  timestampSortFields: [TimestampSortField.CreatedAt, TimestampSortField.UpdatedAt],
  exportType: DataExportType.Customer,
} as const;

const CUSTOMER_TABLE_QUERY_HOOKS: DataFetchHooks<Customer, CustomerQueryParams, CustomerRequestBody> = {
  useSearchQuery: useCustomerSearchQuery,
} as const;

export interface CustomerTableProps extends BasePredefinedTableProps<
  Customer,
  CustomerSorting,
  CustomerQueryParams,
  CustomerRequestBody
> {
  extend?: CustomerExtend;
}

const BaseCustomerTable = createBasePredefinedTable({
  config: CUSTOMER_TABLE_CONFIG,
  queryHooks: CUSTOMER_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

/**
 * The API scopes customers to the company groups granted via `CG.*` attribute app roles. The filter mirrors
 * that scope so users can only narrow down within the groups they are allowed to see. Masterdata administrators
 * can pick any company group; users without any `CG.*` role get no filter because nothing could be selected.
 */
function createScopedCompanyGroupFilter(userRoles: readonly SubjectRoles[] | undefined): CompanyGroupFilter | null {
  if (hasUnrestrictedCustomerAccess(userRoles)) {
    return new CompanyGroupFilter();
  }

  const companyGroupTechnicalIds = getUserCompanyGroupTechnicalIds(userRoles);
  if (companyGroupTechnicalIds.length === 0) {
    return null;
  }

  return new CompanyGroupFilter({ body: { technicalId: companyGroupTechnicalIds } });
}

export function CustomerTable(props: CustomerTableProps) {
  const { t } = useTranslation(["masterdata"]);
  const {
    name = "customer-table",
    showCreateButton = false,
    extend = DEFAULT_EXTEND,
    filters,
    filterValues,
    query,
    onRowNavigate = createRowNavigatePath<Customer>("/masterdata/customer"),
    className,
    ...restProps
  } = props;
  const userRoles = userStore((state) => state.user?.roles);
  const hasCompanyGroupScope = hasCustomerCompanyGroupScope(userRoles);

  const defaultFilters = useMemo(() => {
    if (filters) {
      return filters;
    }

    const companyGroupFilter = createScopedCompanyGroupFilter(userRoles);

    return [
      ...(companyGroupFilter ? [companyGroupFilter] : []),
      new CompanyFilter({ operationalCustomersOnly: true }),
      new BranchFilter({ includeWithoutBranch: true }),
      new CustomerTypeFilter(),
      new CustomerIdentifierTypeFilter(),
    ];
  }, [filters, userRoles]);

  // The API answers with an empty list for users without company group scope; explain that instead of
  // rendering an empty table (and skip the pointless request).
  if (!hasCompanyGroupScope) {
    return <CustomerCompanyGroupScopeEmptyState className={className} />;
  }

  return (
    <BaseCustomerTable
      searchPlaceholder={t("masterdata:SearchCustomers")}
      name={name}
      showCreateButton={showCreateButton}
      extend={extend}
      filters={defaultFilters}
      filterValues={filterValues}
      query={query}
      onRowNavigate={onRowNavigate}
      customRowActions={null}
      className={className}
      {...restProps}
    />
  );
}
